import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { fetchMe, loginRedirect, logout } from "../lib/api";
import { LogoLockup } from "./Logo";
import { Sparkles, LogOut, Menu, X, ChevronDown } from "lucide-react";

function NavItem({ to, label, caret, testid, dropdown }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="relative"
      onMouseEnter={() => dropdown && setOpen(true)}
      onMouseLeave={() => dropdown && setOpen(false)}
    >
      <Link
        to={to}
        data-testid={testid}
        className="inline-flex items-center gap-1 hover:text-[#B3833B] transition-colors font-medium"
      >
        {label}
        {caret && <ChevronDown className="w-3.5 h-3.5 opacity-60" />}
      </Link>
      {dropdown && open && (
        <div
          className="absolute left-0 top-full pt-2 min-w-[220px]"
          data-testid={`${testid}-menu`}
        >
          <div className="rounded-xl border border-[#0B1020]/10 bg-white shadow-lg overflow-hidden">
            {dropdown.map((item) => (
              <Link
                key={item.to + item.label}
                to={item.to}
                data-testid={`${testid}-item-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="block px-4 py-2.5 text-sm text-[#0B1020] hover:bg-[#FCFAF5] hover:text-[#B3833B] transition"
              >
                {item.label}
                {item.desc && <div className="text-[11px] text-slate-500 mt-0.5">{item.desc}</div>}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Nav() {
  const [user, setUser] = useState(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return;
    fetchMe().then(setUser);
  }, [location.pathname]);

  const doLogout = async () => {
    await logout();
    setUser(null);
    navigate("/");
  };

  return (
    <>
    <header
      data-testid="site-nav"
      className="sticky top-0 z-40 bg-white/80 backdrop-blur-lg border-b border-[#1E1B7A]/10"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-3 sm:px-8 py-3 gap-2">
        <Link to="/" data-testid="nav-logo" className="flex items-center min-w-0 shrink">
          <LogoLockup size={44} />
        </Link>

        <nav className="hidden md:flex items-center gap-7 text-sm text-[#0B1020]">
          {user?.has_paid && (<>
          <NavItem to="/kundli/calculator" label="Kundli" caret testid="nav-kundli"
                   dropdown={[
                     { to: "/kundli/calculator", label: "Cast New Kundli", desc: "Enter birth details" },
                     { to: "/predictions", label: "My Saved Kundlis", desc: "Open a living dashboard" },
                     { to: "/transits/today", label: "Today's Transits", desc: "Live planetary positions" },
                   ]} />
          <NavItem to="/predictions" label="Predictions" caret testid="nav-predictions"
                   dropdown={[
                     { to: "/predictions", label: "Overview" },
                     { to: "/predictions", label: "Wealth & Income" },
                     { to: "/predictions", label: "Career" },
                     { to: "/predictions", label: "Timing" },
                     { to: "/predictions", label: "Opportunity Calendar" },
                   ]} />
          </>)}
          <NavItem to="/reports" label="Reports" caret testid="nav-reports"
                   dropdown={[
                     { to: "/reports/vedic", label: "Vedic Kundli Report" },
                     { to: "/reports/career", label: "Career Report" },
                     { to: "/reports/wealth", label: "Wealth Report" },
                     { to: "/reports/love", label: "Love Report" },
                     { to: "/reports", label: "See all 10 reports" },
                   ]} />
          {user?.has_paid && <NavItem to="/predictions" label="Ask AI" testid="nav-askai" />}
          <NavItem to="/blog" label="Blog" testid="nav-blog" />
          <NavItem to="/reports" label="Pricing" testid="nav-pricing" />
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <>
              <span className="text-xs text-[#667085]" data-testid="nav-user-email">{user.email}</span>
              <button data-testid="nav-logout-btn" onClick={doLogout}
                className="inline-flex items-center gap-1.5 rounded-full border border-[#0B1020]/15 px-3 py-1.5 text-xs text-[#0B1020] hover:border-[#C6A15B] hover:text-[#B3833B] transition">
                <LogOut className="w-3.5 h-3.5" /> Sign out
              </button>
            </>
          ) : (
            <>
              <button data-testid="nav-login-btn" onClick={loginRedirect}
                className="rounded-full border border-[#0B1020]/25 px-5 py-2 text-sm font-medium text-[#0B1020] hover:border-[#C6A15B] hover:text-[#B3833B] transition">
                Login
              </button>
              <button data-testid="nav-get-started-btn" onClick={loginRedirect}
                className="btn-gold rounded-full px-5 py-2 text-sm font-medium">
                Get Started
              </button>
            </>
          )}
        </div>

        <button data-testid="nav-menu-btn" aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open} className="md:hidden text-[#1E1B7A] p-2 -mr-1 rounded-lg hover:bg-[#1E1B7A]/5 shrink-0" onClick={() => setOpen(!open)}>
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-[#1E1B7A]/10 bg-white shadow-lg">          <div className="px-6 py-5 space-y-3 text-sm text-[#334155]">
            {user?.has_paid && <Link to="/kundli/calculator" onClick={() => setOpen(false)} className="block py-2 border-b border-gray-100">Kundli</Link>}
            {user?.has_paid && <Link to="/predictions" onClick={() => setOpen(false)} className="block py-2 border-b border-gray-100">Predictions</Link>}
            <Link to="/reports" onClick={() => setOpen(false)} className="block py-2 border-b border-gray-100">Reports</Link>
            {user?.has_paid && <Link to="/predictions" onClick={() => setOpen(false)} className="block py-2 border-b border-gray-100">Ask AI</Link>}
            <Link to="/blog" onClick={() => setOpen(false)} className="block py-2 border-b border-gray-100">Blog</Link>
            <Link to="/reports" onClick={() => setOpen(false)} className="block py-2 border-b border-gray-100">Pricing</Link>
            {user && (
              <Link to="/dashboard" onClick={() => setOpen(false)} className="block py-2 border-b border-gray-100">Dashboard</Link>
            )}
          </div>
          <div className="px-6 pb-6">
            {user ? (
              <button onClick={doLogout} data-testid="mobile-logout-btn" className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-gray-300 py-3 text-sm">
                <LogOut className="w-4 h-4" /> Sign out
              </button>
            ) : (
              <button onClick={loginRedirect} data-testid="mobile-login-btn"
                className="w-full inline-flex items-center justify-center gap-3 rounded-full bg-white border-2 border-gray-200 shadow-md active:shadow-sm active:scale-[0.98] transition py-3 font-medium text-[#3C4043] text-base">
                <svg width="20" height="20" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                </svg>
                Sign in with Google
              </button>
            )}
          </div>
        </div>
      )}
    </header>

    {/* Mobile persistent sign-in FAB — bottom-center, outside sticky header so it truly floats */}
    {!user && (
      <button onClick={loginRedirect} data-testid="mobile-nav-signin-badge"
        style={{ position: "fixed", bottom: "20px", left: "50%", transform: "translateX(-50%)", zIndex: 60 }}
        className="md:hidden inline-flex items-center gap-2 rounded-full bg-white border border-gray-200 shadow-xl active:scale-95 transition px-5 py-2.5 text-sm font-medium text-[#3C4043]"
        aria-label="Sign in with Google">
        <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
          <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
          <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
          <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
          <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
        </svg>
        Sign in with Google
      </button>
    )}
    </>
  );
}
